import { DayOfWeek, TemporalAdjusters } from "@js-joda/core";
import type { LocalDate } from "@js-joda/core";

import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import messages, { keys } from "@/plugins/i18n";
import previousOrSame = TemporalAdjusters.previousOrSame;
import nextOrSame = TemporalAdjusters.nextOrSame;
import firstDayOfYear = TemporalAdjusters.firstDayOfYear;
import lastDayOfYear = TemporalAdjusters.lastDayOfYear;
import lastDayOfMonth = TemporalAdjusters.lastDayOfMonth;
import firstDayOfMonth = TemporalAdjusters.firstDayOfMonth;

export interface DateRangePreset {
  key: string;
  label: string;

  getRange(): [fromDate: LocalDate, toDate: LocalDate];
}

export const today: DateRangePreset = {
  key: keys.date_range_today.value,
  label: messages.date_range_today,
  getRange() {
    return [tenantService.today, tenantService.today];
  },
};
export const yesterday: DateRangePreset = {
  key: keys.date_range_yesterday.value,
  label: messages.date_range_yesterday,
  getRange() {
    return [tenantService.today.minusDays(1), tenantService.today.minusDays(1)];
  },
};
export const lastSevenDays: DateRangePreset = {
  key: keys.date_range_last_seven_days.value,
  label: messages.date_range_last_seven_days,
  getRange() {
    // Includes today (Product decision based on reporting standard)
    return [tenantService.today.minusDays(6), tenantService.today];
  },
};
export const lastThirtyDays: DateRangePreset = {
  key: keys.date_range_last_thirty_days.value,
  label: messages.date_range_last_thirty_days,
  getRange() {
    return [tenantService.today.minusDays(29), tenantService.today];
  },
};
export const lastSixtyDays: DateRangePreset = {
  key: keys.date_range_last_sixty_days.value,
  label: messages.date_range_last_sixty_days,
  getRange() {
    return [tenantService.today.minusDays(59), tenantService.today];
  },
};
export const thisWeek: DateRangePreset = {
  key: keys.date_range_this_week.value,
  label: messages.date_range_this_week,
  getRange() {
    return [
      tenantService.today.with(previousOrSame(DayOfWeek.MONDAY)),
      tenantService.today.with(nextOrSame(DayOfWeek.SUNDAY)),
    ];
  },
};
export const lastWeek: DateRangePreset = {
  key: keys.date_range_last_week.value,
  label: messages.date_range_last_week,
  getRange() {
    return [
      tenantService.today.minusWeeks(1).with(previousOrSame(DayOfWeek.MONDAY)),
      tenantService.today.minusWeeks(1).with(nextOrSame(DayOfWeek.SUNDAY)),
    ];
  },
};
export const thisMonth: DateRangePreset = {
  key: keys.date_range_this_month.value,
  label: messages.date_range_this_month,
  getRange() {
    return [
      tenantService.today.with(firstDayOfMonth()),
      tenantService.today.with(lastDayOfMonth()),
    ];
  },
};
export const lastMonth: DateRangePreset = {
  key: keys.date_range_last_month.value,
  label: messages.date_range_last_month,
  getRange() {
    return [
      tenantService.today.minusMonths(1).with(firstDayOfMonth()),
      tenantService.today.minusMonths(1).with(lastDayOfMonth()),
    ];
  },
};
export const thisYear: DateRangePreset = {
  key: keys.date_range_this_year.value,
  label: messages.date_range_this_year,
  getRange() {
    return [
      tenantService.today.with(firstDayOfYear()),
      tenantService.today.with(lastDayOfYear()),
    ];
  },
};
export const lastYear: DateRangePreset = {
  key: keys.date_range_last_year.value,
  label: messages.date_range_last_year,
  getRange() {
    return [
      tenantService.today.minusYears(1).with(firstDayOfYear()),
      tenantService.today.minusYears(1).with(lastDayOfYear()),
    ];
  },
};

export const nextWeek: DateRangePreset = {
  key: keys.date_range_next_week.value,
  label: messages.date_range_next_week,
  getRange() {
    return [
      tenantService.today.plusWeeks(1).with(previousOrSame(DayOfWeek.MONDAY)),
      tenantService.today.plusWeeks(1).with(nextOrSame(DayOfWeek.SUNDAY)),
    ];
  },
};

export const nextMonth: DateRangePreset = {
  key: keys.date_range_next_month.value,
  label: messages.date_range_next_month,
  getRange() {
    return [
      tenantService.today.plusMonths(1).with(firstDayOfMonth()),
      tenantService.today.plusMonths(1).with(lastDayOfMonth()),
    ];
  },
};

export const nextYear: DateRangePreset = {
  key: keys.date_range_next_year.value,
  label: messages.date_range_next_year,
  getRange() {
    return [
      tenantService.today.plusYears(1).with(firstDayOfYear()),
      tenantService.today.plusYears(1).with(lastDayOfYear()),
    ];
  },
};

export const tomorrow: DateRangePreset = {
  key: keys.date_range_tomorrow.value,
  label: messages.date_range_tomorrow,
  getRange() {
    return [tenantService.today.plusDays(1), tenantService.today.plusDays(1)];
  },
};

/**
 * Array of presets to display in the date range control
 */

const allPresets: DateRangePreset[] = [
  today,
  yesterday,
  lastSevenDays,
  lastThirtyDays,
  lastSixtyDays,
  thisWeek,
  lastWeek,
  thisMonth,
  lastMonth,
  thisYear,
  lastYear,
];

export default allPresets;
